import { t } from '../../javascript/lib/i18n';
import { extractCellText, extractFilterKey, extractSortValue } from './helpers';

// Filter popup.
//
// Column config `filter: { type: 'checkbox' }`, `'keywords'`, or `'number'`
// opts a column into filtering. We translate it into Tabulator's `headerPopup` +
// `headerPopupIcon` options. Tabulator's Popup module handles the button
// insertion, click-to-open, and close-on-blur; we just supply the popup
// content and icon.
//
// Checkbox popup contents (built by `buildCheckboxFilterPopup`):
//   * search input — narrows the visible checkboxes in the popup (a UI
//     aid when the option list is long). Doesn't filter the table itself.
//   * `Select all` / `Clear` buttons that toggle every option
//   * a checkbox list of options — explicit `filter.options` if the caller
//     passed one, otherwise the unique text values present in the column.
//     Options default to all-checked (nothing hidden until the user narrows).
//
// Only the checkboxes drive `Tabulator.setFilter`: a row is visible iff
// every filtered column's cell value matches one of the checked options.
// Checkbox state lives on the Stimulus controller (per-column) so it
// survives the popup being closed and re-opened.
export function buildFilterColumnOptions(_col, filter, controller) {
  return {
    headerPopupIcon: '<i class="fas fa-filter fs-tiny" aria-hidden="true"></i>',
    headerPopup: (_e, component, onRendered) => {
      const popup = buildFilterPopup(component, filter, controller);
      onRendered(() => focusFirstFilterField(popup));
      return popup;
    },
  };
}

function buildFilterPopup(component, filter, controller) {
  if (filter.type === 'number') {
    return buildNumberFilterPopup(component, filter, controller);
  }
  if (filter.type === 'keywords') {
    return buildKeywordsFilterPopup(component, filter, controller);
  }
  return buildCheckboxFilterPopup(component, filter, controller);
}

function focusFirstFilterField(popup) {
  // Checkbox popup: search input is the natural starting point. Number
  // popup: the value input — operator defaults to "Equals" and most users
  // will just want to type a number.
  const target =
    popup.querySelector('.amba-tabulator-filter-search') ??
    popup.querySelector('.amba-tabulator-filter-number-value');
  target?.focus();
}

// Coerce both option-shapes the caller may pass into a single internal
// shape: `[{label: string, value: string}]`.
//
//   - Strings: used as both label and value, with cell-text trimming so
//     an explicit `filter.options` list like `['Admin']` matches cells
//     rendered as `<span>Admin&nbsp;</span>`.
//   - Objects: `{label, value}` passed through; `value` coerced to
//     String so it round-trips through URL params (e.g. user IDs).
//
// NOTE: local-mode `matchesCheckboxFilter` compares `state.checkedValues`
// against `extractFilterKey(rawValue)` — the raw, undecoded wrapper
// attribute or the cell text. Auto-derived local options therefore arrive
// as `{label, value}` objects keyed by that same raw key, with the decoded
// text as the label, so entities and edge whitespace can't split them.
export function normaliseFilterOptions(rawOptions) {
  return rawOptions
    .map((opt) => {
      if (opt && typeof opt === 'object') {
        return { label: String(opt.label), value: String(opt.value) };
      }
      const text = extractCellText(opt);
      return { label: text, value: text };
    })
    .filter((o) => o.label && o.value !== '');
}

// Number filter operator vocabulary mirrors Tabulator's built-in filter
// operators (`=`, `!=`, `>`, `>=`, `<`, `<=`) plus our own `empty` /
// `not-empty` which Tabulator doesn't ship. We run everything through
// our single function predicate via `setFilter`, so the operator strings
// are only consumed by `NUMBER_OPERATOR_FNS` below — but keeping them
// aligned with Tabulator's vocabulary keeps the popup readable for
// anyone already familiar with Tabulator filters.
function numberOperators() {
  return [
    {
      value: '=',
      label: t('js.tabulator_table.filters.operators.equals'),
      symbol: '=',
      needsValue: true,
    },
    {
      value: '!=',
      label: t('js.tabulator_table.filters.operators.not_equals'),
      symbol: '≠',
      needsValue: true,
    },
    {
      value: '>',
      label: t('js.tabulator_table.filters.operators.greater_than'),
      symbol: '>',
      needsValue: true,
    },
    {
      value: '>=',
      label: t('js.tabulator_table.filters.operators.greater_or_equal'),
      symbol: '≥',
      needsValue: true,
    },
    {
      value: '<',
      label: t('js.tabulator_table.filters.operators.less_than'),
      symbol: '<',
      needsValue: true,
    },
    {
      value: '<=',
      label: t('js.tabulator_table.filters.operators.less_or_equal'),
      symbol: '≤',
      needsValue: true,
    },
    {
      value: 'empty',
      label: t('js.tabulator_table.filters.operators.empty'),
      symbol: '∅',
      needsValue: false,
    },
    {
      value: 'not-empty',
      label: t('js.tabulator_table.filters.operators.not_empty'),
      symbol: '●',
      needsValue: false,
    },
  ];
}

const NUMBER_OPERATOR_FNS = {
  '=': (cell, value) => cell === value,
  '!=': (cell, value) => cell !== value,
  '>': (cell, value) => cell > value,
  '>=': (cell, value) => cell >= value,
  '<': (cell, value) => cell < value,
  '<=': (cell, value) => cell <= value,
  empty: (cell) => Number.isNaN(cell),
  'not-empty': (cell) => !Number.isNaN(cell),
};

function findOperator(value) {
  return numberOperators().find((op) => op.value === value);
}

// Tabulator's PopupModule registers an Escape handler on `document.body`,
// but its key check (`e.key == 27`) compares the string "Escape" against
// the legacy keyCode 27 — always false, so Escape never closes a popup
// out of the box. Route Escape through Tabulator's blur-close path
// instead: dispatching `mousedown` on `document.body` triggers the
// listener Tabulator binds in `hideOnBlur`, which calls `popup.hide()`.
// Restoring focus to the filter button afterwards keeps keyboard users
// anchored once the popup is gone.
//
// Tabulator binds that body `mousedown` listener inside a 100ms setTimeout
// (see Popup.hideOnBlur in tabulator_esm.js). On a fast runner — Capybara
// on CI is the case in point — the user can press Escape before the
// listener is bound, leaving the first dispatch a no-op and the popup
// stuck open. Retry once after the 100ms window has elapsed so the close
// path is deterministic regardless of how quickly Escape lands.
function attachEscapeCloseHandler(popupEl, column) {
  popupEl.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    const btn = column
      .getElement()
      .querySelector('.tabulator-header-popup-button');
    document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
    if (popupEl.isConnected) {
      setTimeout(() => {
        if (popupEl.isConnected) {
          document.body.dispatchEvent(
            new MouseEvent('mousedown', { bubbles: true })
          );
        }
      }, 110);
    }
    btn?.focus();
  });
}

function buildNumberFilterPopup(column, _filter, controller) {
  const field = column.getField();
  const title = column.getDefinition().title ?? field;
  const state = controller.getOrInitFilterState(field, 'number', () => ({
    kind: 'number',
    operator: '=',
    value: '',
  }));

  const el = document.createElement('div');
  el.classList.add(
    'amba-tabulator-filter-popup',
    'amba-tabulator-filter-number'
  );
  el.setAttribute('role', 'dialog');
  el.setAttribute(
    'aria-label',
    t('js.tabulator_table.filters.filter_column', { title })
  );
  // Same body-level confirm-navigation opt-out as the checkbox popup —
  // typing in the value input would otherwise mark the page dirty.
  el.dataset.confirmNavigationIgnoreInputWithin = '';

  const opRow = document.createElement('div');
  opRow.className = 'amba-tabulator-filter-number-row';

  const opIcon = document.createElement('span');
  opIcon.className = 'amba-tabulator-filter-number-icon';
  opIcon.setAttribute('aria-hidden', 'true');
  opIcon.textContent = findOperator(state.operator)?.symbol ?? '';

  const opSelect = document.createElement('select');
  opSelect.className = 'form-select form-select-sm';
  opSelect.setAttribute(
    'aria-label',
    t('js.tabulator_table.filters.filter_comparison', { title })
  );
  numberOperators().forEach((op) => {
    const opt = document.createElement('option');
    opt.value = op.value;
    opt.textContent = op.label;
    if (op.value === state.operator) opt.selected = true;
    opSelect.appendChild(opt);
  });

  opRow.append(opIcon, opSelect);

  const valRow = document.createElement('div');
  valRow.className = 'amba-tabulator-filter-number-row';

  const valIcon = document.createElement('i');
  valIcon.className = 'fas fa-search amba-tabulator-filter-number-icon';
  valIcon.setAttribute('aria-hidden', 'true');

  const valInput = document.createElement('input');
  valInput.type = 'number';
  valInput.className =
    'form-control form-control-sm amba-tabulator-filter-number-value';
  valInput.placeholder = t('js.tabulator_table.filters.value_placeholder');
  valInput.setAttribute(
    'aria-label',
    t('js.tabulator_table.filters.filter_value', { title })
  );
  valInput.value = state.value;

  valRow.append(valIcon, valInput);
  el.append(opRow, valRow);

  const updateValueRowVisibility = (operator) => {
    valRow.hidden = !findOperator(operator)?.needsValue;
  };
  updateValueRowVisibility(state.operator);

  attachEscapeCloseHandler(el, column);

  opSelect.addEventListener('change', () => {
    state.operator = opSelect.value;
    opIcon.textContent = findOperator(state.operator)?.symbol ?? '';
    updateValueRowVisibility(state.operator);
    controller.applyFilters();
  });

  valInput.addEventListener('input', () => {
    state.value = valInput.value;
    controller.applyFilters();
  });

  return el;
}

function buildKeywordsFilterPopup(column, filter, controller) {
  const field = column.getField();
  const title = column.getDefinition().title ?? field;
  const options = normaliseFilterOptions(
    filter.options ?? extractUniqueColumnValues(column)
  );
  const state = controller.getOrInitFilterState(field, 'keywords', () => ({
    kind: 'keywords',
    checkedValues: new Set(options.map((o) => o.value)),
    allOptions: options,
  }));

  const el = document.createElement('div');
  el.classList.add(
    'amba-tabulator-filter-popup',
    'amba-tabulator-filter-keywords'
  );
  el.setAttribute('role', 'dialog');
  el.setAttribute(
    'aria-label',
    t('js.tabulator_table.filters.filter_column', { title })
  );
  el.dataset.confirmNavigationIgnoreInputWithin = '';

  const actions = buildFilterActions();
  const divider = document.createElement('hr');
  divider.classList.add('amba-tabulator-filter-divider');
  const optionsList = buildFilterOptionsList(
    field,
    options,
    state.checkedValues
  );

  el.append(actions, divider, optionsList);

  attachEscapeCloseHandler(el, column);

  optionsList.querySelectorAll('.form-check-input').forEach((input) => {
    input.addEventListener('change', () => {
      if (input.checked) state.checkedValues.add(input.value);
      else state.checkedValues.delete(input.value);
      controller.applyFilters();
    });
  });

  actions
    .querySelector('[data-action="select-all"]')
    .addEventListener('click', () => {
      setAllOptionsChecked(optionsList, true);
      options.forEach((o) => state.checkedValues.add(o.value));
      controller.applyFilters();
    });
  actions
    .querySelector('[data-action="deselect"]')
    .addEventListener('click', () => {
      setAllOptionsChecked(optionsList, false);
      state.checkedValues.clear();
      controller.applyFilters();
    });

  return el;
}

function buildCheckboxFilterPopup(column, filter, controller) {
  const field = column.getField();
  const title = column.getDefinition().title ?? field;
  // Normalise to `[{label, value}]` regardless of input shape:
  //   - strings (a string list from the caller) → `{label: s, value: s}`,
  //     with `extractCellText` trimming/HTML-stripping.
  //   - `{label, value}` objects (local-mode auto-derived from cells, keyed
  //     by the raw filter key; or remote-mode, where the display name
  //     differs from the filter value — e.g. user names keyed by id) →
  //     passed through; `value` coerced to String so it round-trips
  //     through the URL.
  // The popup, state, and URL-param emission all work off the unified
  // shape so neither side has to special-case.
  // `multiValue` columns hold several values per cell (e.g. a guide's roles),
  // pipe-separated in `data-filter-value`. Each one is its own option, and a
  // row passes when any of them is ticked.
  const multiValue = filter.multiValue === true;
  const options = normaliseFilterOptions(
    filter.options ?? extractUniqueColumnValues(column, controller)
  );
  const state = controller.getOrInitFilterState(field, 'checkbox', () => ({
    kind: 'checkbox',
    checkedValues: new Set(options.map((o) => o.value)),
    allOptions: options,
    multiValue,
  }));

  const el = document.createElement('div');
  el.classList.add('amba-tabulator-filter-popup');
  el.setAttribute('role', 'dialog');
  el.setAttribute(
    'aria-label',
    t('js.tabulator_table.filters.filter_column', { title })
  );
  // The popup is appended to `document.body` by Tabulator's PopupModule, so
  // its inputs bubble straight to the body-level `confirm-navigation`
  // controller (wired in the application layout). Without this opt-out,
  // typing in the search box or toggling a checkbox marks the page dirty
  // and prompts on navigate-away.
  el.dataset.confirmNavigationIgnoreInputWithin = '';

  const search = buildFilterSearchInput(title);
  const actions = buildFilterActions();
  const divider = document.createElement('hr');
  divider.classList.add('amba-tabulator-filter-divider');
  const optionsList = buildFilterOptionsList(
    field,
    options,
    state.checkedValues
  );

  el.append(search, actions, divider, optionsList);

  attachEscapeCloseHandler(el, column);

  // Search is purely a popup-local UI aid — hides checkboxes whose label
  // doesn't match so the list stays scannable when there are many
  // options. It doesn't touch the table's rows, so the `input` event
  // doesn't need to update controller state or re-apply filters.
  search.addEventListener('input', () => {
    filterOptionsBySearch(optionsList, search.value);
  });

  optionsList.querySelectorAll('.form-check-input').forEach((input) => {
    input.addEventListener('change', () => {
      if (input.checked) state.checkedValues.add(input.value);
      else state.checkedValues.delete(input.value);
      controller.applyFilters();
    });
  });

  actions
    .querySelector('[data-action="select-all"]')
    .addEventListener('click', () => {
      setAllOptionsChecked(optionsList, true);
      options.forEach((o) => state.checkedValues.add(o.value));
      controller.applyFilters();
    });
  actions
    .querySelector('[data-action="deselect"]')
    .addEventListener('click', () => {
      setAllOptionsChecked(optionsList, false);
      state.checkedValues.clear();
      controller.applyFilters();
    });

  return el;
}

function buildFilterSearchInput(title) {
  const input = document.createElement('input');
  input.type = 'search';
  input.className = 'form-control form-control-sm amba-tabulator-filter-search';
  input.placeholder = t('js.common.search_placeholder');
  // Keep the column title on the accessible label so screen-reader users
  // still know which column they're searching even though the visible
  // placeholder is generic.
  input.setAttribute(
    'aria-label',
    t('js.tabulator_table.search_column', { title })
  );
  return input;
}

function buildFilterActions() {
  const wrap = document.createElement('div');
  wrap.classList.add('amba-tabulator-filter-actions');

  const selectAll = document.createElement('button');
  selectAll.type = 'button';
  selectAll.className = 'btn btn-link btn-sm p-0';
  selectAll.dataset.action = 'select-all';
  selectAll.textContent = t('js.tabulator_table.filters.select_all');

  const deselect = document.createElement('button');
  deselect.type = 'button';
  deselect.className = 'btn btn-link btn-sm p-0';
  deselect.dataset.action = 'deselect';
  deselect.textContent = t('js.tabulator_table.filters.deselect');

  wrap.append(selectAll, deselect);
  return wrap;
}

function buildFilterOptionsList(field, options, checkedValues) {
  const list = document.createElement('div');
  list.classList.add('amba-tabulator-filter-options');
  // SimpleBar via `permanent-scrollbar` gives the list an always-visible
  // scrollbar so users can see at a glance that more options exist below
  // — native scrollbars auto-hide on Chrome/Safari mobile and the popup
  // otherwise looks "complete" when it's actually overflowing.
  list.setAttribute('data-controller', 'permanent-scrollbar');

  options.forEach(({ label: labelText, value }, i) => {
    const id = `amba-tabulator-filter-${field}-${i}`;
    const row = document.createElement('div');
    row.className = 'form-check';

    const input = document.createElement('input');
    input.className = 'form-check-input';
    input.type = 'checkbox';
    input.id = id;
    input.value = value;
    input.checked = checkedValues.has(value);

    const label = document.createElement('label');
    label.className = 'form-check-label';
    label.htmlFor = id;
    // The "empty" option's internal value is the EMPTY_FILTER_VALUE
    // sentinel — show users a readable "None" label instead.
    label.textContent =
      value === EMPTY_FILTER_VALUE ? emptyFilterLabel() : labelText;
    if (value === EMPTY_FILTER_VALUE)
      label.classList.add('text-muted', 'fst-italic');

    row.append(input, label);
    list.append(row);
  });

  return list;
}

// Global-search predicate — returns `true` iff the row's text in any of
// the named fields contains `query` (case-insensitive substring match).
// Caller passes `query` already lower-cased and trimmed; we avoid
// re-normalising on every row. Empty queries are short-circuited upstream
// (in `applyFilters`).
export function matchesGlobalSearch(rowData, searchFields, query) {
  return searchFields.some((field) => {
    const text = extractCellText(rowData[field]).toLowerCase();
    return text.includes(query);
  });
}

// Grouped tables: while a filter is active, expand every collapsed group that
// has rows. With `hideEmpty`, also hide each group header with no matching row
// (declared `groupValues` render empty groups too) until the filter clears.
// `group.hide()` only collapses and `group.getRows()` ignores the filter,
// hence the `'active'` row set and `display: none` on the header element.
export function syncGroupVisibility(tabulator, hasActiveFilter, hideEmpty) {
  const activeGroupKeys =
    hideEmpty && hasActiveFilter
      ? new Set(
          tabulator
            .getRows('active')
            .map((row) => String(row.getData()._rowGroup))
        )
      : null;

  let expanded = false;
  tabulator.getGroups().forEach((group) => {
    const hasRows = activeGroupKeys
      ? activeGroupKeys.has(String(group.getKey()))
      : group.getRows().length > 0;
    if (hideEmpty) {
      group.getElement().style.display =
        !hasActiveFilter || hasRows ? '' : 'none';
    }
    if (hasActiveFilter && hasRows && !group.isVisible()) {
      group.show();
      expanded = true;
    }
  });
  if (expanded) tabulator.redraw(true);
}

// Per-row predicate used inside `setFilter`. Switches on the criteria's
// `kind` so `applyFilters` doesn't have to know how each filter type
// interprets a row.
export function matchesFilter(rawValue, criteria) {
  if (criteria.kind === 'keywords')
    return matchesKeywordsFilter(rawValue, criteria);
  if (criteria.kind === 'number')
    return matchesNumberFilter(rawValue, criteria);
  if (criteria.kind === 'search')
    return matchesSearchFilter(rawValue, criteria);
  return matchesCheckboxFilter(rawValue, criteria);
}

function matchesKeywordsFilter(rawValue, criteria) {
  const isNarrowing =
    criteria.checkedValues.size !== criteria.allOptions.length;
  if (!isNarrowing) return true;
  if (criteria.checkedValues.size === 0) return false;

  const text = extractFilterKey(rawValue).toLowerCase();
  return [...criteria.checkedValues].some((keyword) =>
    text.includes(keyword.toLowerCase())
  );
}

// Per-column search predicate: the row passes iff the column's visible cell
// text contains the query (case-insensitive substring). An empty / blank
// query is inert — every row passes — so opening a popup without typing
// never narrows the table.
function matchesSearchFilter(rawValue, criteria) {
  const query = criteria.query.trim().toLowerCase();
  if (query === '') return true;
  return extractCellText(rawValue).toLowerCase().includes(query);
}

function matchesCheckboxFilter(rawValue, criteria) {
  const isNarrowing =
    criteria.checkedValues.size !== criteria.allOptions.length;
  if (!isNarrowing) return true;
  // Blank cells (no text after HTML strip) are represented in the option
  // list by `EMPTY_FILTER_VALUE`; translate here so the membership check
  // works against the sentinel rather than ''.
  const text = extractFilterKey(rawValue);
  if (criteria.multiValue) {
    const keys = splitMultiValue(text);
    if (keys.length === 0)
      return criteria.checkedValues.has(EMPTY_FILTER_VALUE);
    return keys.some((key) => criteria.checkedValues.has(key));
  }
  const key = text === '' ? EMPTY_FILTER_VALUE : text;
  return criteria.checkedValues.has(key);
}

// Separator for a `multiValue` column's cell key. A pipe rather than a comma:
// it doesn't occur in the labels we filter by and doesn't read as punctuation
// inside one.
const MULTI_VALUE_SEPARATOR = '|';

function splitMultiValue(text) {
  return text
    .split(MULTI_VALUE_SEPARATOR)
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

function matchesNumberFilter(rawValue, criteria) {
  const opDef = findOperator(criteria.operator);
  if (!opDef) return true;
  // Numeric operators stay inert until the user types a number — empty /
  // not-empty have no value field to wait on, so they apply immediately.
  if (opDef.needsValue && criteria.value === '') return true;

  const cellNumber = parseSortValueToNumber(rawValue);
  const filterNumber = parseFloat(criteria.value);
  return NUMBER_OPERATOR_FNS[criteria.operator](cellNumber, filterNumber);
}

// Reads the `data-sort-value` already attached to numeric cells (the same
// attribute the overridden `number` sorter reads) and returns it as a
// JS number, or NaN when the cell has no parseable numeric value — the
// "empty" state that the empty/not-empty operators key off.
function parseSortValueToNumber(rawValue) {
  const sortValue = extractSortValue(rawValue);
  if (sortValue === null || sortValue === undefined || sortValue === '') {
    return NaN;
  }
  const num = parseFloat(sortValue);
  return Number.isFinite(num) ? num : NaN;
}

// `true` when the column's current filter would actually exclude rows —
// drives the active visual state on the filter button. For checkbox, that
// means at least one option is unchecked. For number, it means the
// operator is set and (for numeric ops) a value has been typed.
export function isFilterActive(criteria) {
  if (!criteria) return false;
  if (criteria.kind === 'keywords') {
    return criteria.checkedValues.size !== criteria.allOptions.length;
  }
  if (criteria.kind === 'number') {
    const opDef = findOperator(criteria.operator);
    if (!opDef) return false;
    return !opDef.needsValue || criteria.value !== '';
  }
  if (criteria.kind === 'search') return criteria.query.trim() !== '';
  return criteria.checkedValues.size !== criteria.allOptions.length;
}

// Read the column's filter key out of every row and return one `{label,
// value}` option per unique key, sorted by its decoded text label. Used when
// the caller doesn't pass `filter.options` — the filter UI derives its
// options from whatever's actually present in the data.
//
// Reads the table's data rather than the column's cells: Tabulator only holds
// cells for rows it has rendered, so on a paginated table `column.getCells()`
// sees the current page (plus whatever pages were rendered before it) and the
// popup would offer a value list that changes as the user pages. `getData()`
// returns every row, unfiltered and across all pages, which is the option list
// users expect — and it keeps the list stable while filters narrow the table.
// Heading rows are decorative labels, not data, so they contribute nothing.
//
// When any row is blank in the column, `EMPTY_FILTER_VALUE` is prepended
// to the list so users can include / exclude blank rows the same way they
// would any other value. Without this, "Deselect all" leaves blank rows
// hidden with no checkbox the user could re-tick to bring them back.
function extractUniqueColumnValues(column, controller) {
  const field = column.getField();
  const labelsByKey = new Map();
  let hasEmpty = false;
  (controller.tabulator?.getData() ?? []).forEach((rowData) => {
    if (rowData._heading) return;
    const key = extractFilterKey(rowData[field]);
    if (!key) hasEmpty = true;
    else if (!labelsByKey.has(key)) labelsByKey.set(key, extractCellText(key));
  });
  const sorted = [...labelsByKey]
    .map(([value, label]) => ({ label, value }))
    .sort((a, b) => a.label.localeCompare(b.label));
  if (!hasEmpty) return sorted;
  return [{ label: emptyFilterLabel(), value: EMPTY_FILTER_VALUE }, ...sorted];
}

// Sentinel used as the internal value of the "None" checkbox option.
// Cells whose extracted text is '' are matched against this value (see
// `matchesCheckboxFilter`). Picked to be visually distinct in any
// rare label collision and unlikely to appear as real cell text.
export const EMPTY_FILTER_VALUE = '␀';

function emptyFilterLabel() {
  return t('js.tabulator_table.filters.empty_option');
}

function filterOptionsBySearch(list, query) {
  const q = query.trim().toLowerCase();
  list.querySelectorAll('.form-check').forEach((row) => {
    const label = row.querySelector('label')?.textContent.toLowerCase() ?? '';
    row.hidden = q.length > 0 && !label.includes(q);
  });
}

function setAllOptionsChecked(list, checked) {
  list.querySelectorAll('.form-check-input').forEach((input) => {
    input.checked = checked;
  });
}

// Tabulator's Popup module inserts each filter button inside
// `.tabulator-col-title` (two levels deep inside `.tabulator-col-content`),
// which means flex at the header-cell level can't reach it — the button
// can only be positioned relative to the title's own box. Move it up to
// be a direct sibling of `.tabulator-col-sorter` so the three children
// (title-holder, sort button, filter button) all flex naturally at the
// `.tabulator-col-content` level. Click handlers bound by the Popup
// module survive the move because they're attached to the element, not
// its position in the tree.
export function liftFilterButtons(containerEl) {
  containerEl
    .querySelectorAll('.tabulator-header-popup-button')
    .forEach((btn) => {
      const colContent = btn.closest('.tabulator-col-content');
      if (colContent) colContent.appendChild(btn);

      // Tabulator renders the button as a plain <span> with a click
      // handler — not keyboard-focusable and not announced as a button by
      // screen readers. Upgrade to an ARIA button (matches the sort button
      // treatment in `enhanceSortButtons`) so keyboard users can tab to it
      // and activate with Enter/Space.
      btn.setAttribute('role', 'button');
      btn.setAttribute('tabindex', '0');
      btn.setAttribute('aria-haspopup', 'dialog');
      btn.setAttribute('aria-expanded', 'false');
      const colTitle = btn
        .closest('.tabulator-col')
        ?.querySelector('.tabulator-col-title')
        ?.textContent.trim();
      if (colTitle)
        btn.setAttribute(
          'aria-label',
          t('js.tabulator_table.filters.filter_column', { title: colTitle })
        );

      // Dispatch a real MouseEvent (not `btn.click()`) so Tabulator's
      // PopupModule sees non-zero coordinates and positions the popup
      // beneath the button. `btn.click()` synthesises a click with
      // `pageX/pageY === 0`, which Tabulator's `containerEventCoords`
      // reads — landing the popup at the top-left of the page.
      btn.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        const rect = btn.getBoundingClientRect();
        btn.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            clientX: rect.left,
            clientY: rect.bottom,
          })
        );
      });
    });
}

// Toggle a visual active state on each filter button so users can see at a
// glance which columns currently have a filter applied. Driven by the
// controller's per-column filter state Map. Reads only the filter kinds
// (`checkbox` / `number` / `keywords`) — a column can also carry a `search` criteria, but
// that lights the separate column-search button, not the filter button.
export function refreshFilterButtonStates(containerEl, filterState) {
  containerEl.querySelectorAll('.tabulator-col').forEach((col) => {
    const field = col.getAttribute('tabulator-field');
    if (!field) return;
    const kinds = filterState.get(field);
    const state =
      kinds?.get('checkbox') ?? kinds?.get('number') ?? kinds?.get('keywords');
    const btn = col.querySelector('.tabulator-header-popup-button');
    if (!btn) return;
    btn.classList.toggle('amba-filter-active', isFilterActive(state));
  });
}

// Sync the filter button's `aria-expanded` attribute with the popup's
// open/close state. Called from `popupOpened` / `popupClosed` event
// handlers in the controller.
export function setFilterButtonExpanded(component, expanded) {
  const el = component?.getElement?.();
  const btn = el?.querySelector?.('.tabulator-header-popup-button');
  if (btn) btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
}
